import { useEffect, useRef } from 'react';
import { Map as MapLibreMap, Marker, Popup, NavigationControl, GeolocateControl } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';

export const OSM_STYLE = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors',
    },
  },
  layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
};

/**
 * Marker SVG moderno: gota con glow, borde blanco y centro con ícono.
 * color: color principal (naranja/azul/verde)
 * icon: 'home' | 'building' | 'map' | 'dot' (opcional)
 * size: 'sm' | 'md' | 'lg'
 */
export function createMarker(color = '#F97316', { icon = 'home', size = 'md', label } = {}) {
  const dim = size === 'lg' ? 56 : size === 'sm' ? 36 : 46;
  const el = document.createElement('div');
  el.className = 'cnib-marker';
  el.style.width = `${dim}px`;
  el.style.height = `${dim * 1.25}px`;
  el.style.cursor = 'pointer';
  el.style.filter = `drop-shadow(0 6px 12px rgba(15,23,42,0.35))`;
  el.style.transition = 'filter .18s ease';
  el.addEventListener('mouseenter', () => {
    el.style.filter = `drop-shadow(0 10px 18px rgba(15,23,42,0.45)) brightness(1.08)`;
  });
  el.addEventListener('mouseleave', () => {
    el.style.filter = `drop-shadow(0 6px 12px rgba(15,23,42,0.35))`;
  });

  const iconPaths = {
    home: '<path d="M3 10.5 12 3l9 7.5V21a1 1 0 0 1-1 1h-5a1 1 0 0 1-1-1v-5h-4v5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1Z"/>',
    building: '<path d="M4 2h11v20H4Zm2 3h2v2H6Zm0 4h2v2H6Zm0 4h2v2H6Zm4-8h2v2h-2Zm0 4h2v2h-2Zm0 4h2v2h-2Z"/><path d="M17 10h3v12h-3Z"/>',
    map: '<path d="M9 2 3 4v18l6-2 6 2 6-2V2l-6 2Zm0 2 6 2v14l-6-2Z"/>',
    dot: '<circle cx="12" cy="12" r="6"/>',
  };
  const iconPath = iconPaths[icon] || iconPaths.home;

  el.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 60" width="${dim}" height="${dim * 1.25}" style="display:block">
      <defs>
        <linearGradient id="g-${color.replace('#','')}" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stop-color="${color}" stop-opacity="1"/>
          <stop offset="1" stop-color="${color}" stop-opacity=".85"/>
        </linearGradient>
      </defs>
      <path d="M24 2C14.6 2 7 9.6 7 19c0 14.5 17 36 17 36s17-21.5 17-36C41 9.6 33.4 2 24 2Z"
            fill="url(#g-${color.replace('#','')})" stroke="#fff" stroke-width="3"/>
      <circle cx="24" cy="19" r="12" fill="#fff"/>
      <g transform="translate(12 7)" fill="${color}" stroke="none">
        <svg width="24" height="24" viewBox="0 0 24 24">${iconPath}</svg>
      </g>
    </svg>`;
  return el;
}

export default function CnibMap({ center = [-63.18, -17.78], zoom = 5.5, markers = [], onMapClick, className = '', showGeolocate = true }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const onClickRef = useRef(onMapClick);
  onClickRef.current = onMapClick;

  useEffect(() => {
    if (mapRef.current) return;
    const map = new MapLibreMap({
      container: containerRef.current,
      style: OSM_STYLE,
      center,
      zoom,
      attributionControl: false,
    });
    map.addControl(new NavigationControl({ showCompass: false }), 'top-right');
    if (showGeolocate) {
      map.addControl(
        new GeolocateControl({
          positionOptions: { enableHighAccuracy: true },
          trackUserLocation: true,
          showUserLocation: true,
          showAccuracyCircle: true,
        }),
        'top-right',
      );
    }
    map.on('click', (e) => {
      if (onClickRef.current) onClickRef.current({ lat: e.lngLat.lat, lng: e.lngLat.lng });
    });
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const sync = () => {
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      markers.forEach((mk) => {
        const marker = new Marker({
          element: createMarker(mk.color, { icon: mk.icon, size: mk.size, label: mk.label }),
          anchor: 'bottom',
          draggable: !!mk.draggable,
        })
          .setLngLat([mk.lng, mk.lat])
          .addTo(map);
        if (mk.draggable) {
          marker.getElement().style.cursor = 'grab';
          marker.on('dragstart', () => {
            marker.getElement().style.cursor = 'grabbing';
          });
          marker.on('dragend', () => {
            marker.getElement().style.cursor = 'grab';
            const ll = marker.getLngLat();
            if (typeof mk.onDragEnd === 'function') {
              mk.onDragEnd({ lat: ll.lat, lng: ll.lng });
            }
          });
        }
        if (mk.popupHtml) {
          marker.setPopup(
            new Popup({ offset: 36, closeButton: false, className: 'cnib-popup' }).setHTML(mk.popupHtml)
          );
        }
        markersRef.current.push(marker);
      });
    };
    if (map.loaded()) sync();
    else map.once('load', sync);
  }, [markers]);

  return <div ref={containerRef} className={className} data-testid="cnib-map" />;
}
